import { atom } from 'nanostores'
import type {
  ClickActionTarget,
  ClickHandlerDict,
  DemoClickHandler,
  DemoHandlerAdder,
  DemoHandlerRemover,
  DemoState,
  DemoTypeHandler,
  SelectAction,
  SelectionRect,
} from '@guideguide/shared/src/demo/types'
import script from './script'
import { fauxtoshopStore } from '../state/fauxtoshop-store'
import type { FormState } from '@guideguide/core/src/lib/types'
import getBoundingClientRect from '../getBoundingClientRect'

export let clickHandlers: ClickHandlerDict = {}
export let typeHandler: DemoTypeHandler | undefined = undefined
let lastTimeout: ReturnType<typeof setTimeout> | null = null

export const demoStore = atom<DemoState>({
  currentAction: -1,
  isPlaying: false,
  script,
  lastThreeClicks: [],
})

//////////////////////
// Playback Control //
//////////////////////

const doNextAction = () => {
  const state = demoStore.get()
  const { currentAction, script } = state
  const i = currentAction < script.length - 1 ? currentAction + 1 : 0

  demoStore.set({ ...state, currentAction: i })

  const action = script[i]

  switch (action.type) {
    case 'wait':
      doWaitAction(action.ms)
      break
    case 'click':
      doClickAction(action.target)
      break
    case 'type':
      doTypeAction(action.field, action.text)
      break
    case 'select':
      doSelectAction(action.rect)
      break
    default:
    // noop
  }
}

export const startDemo = () => {
  const state = demoStore.get()
  if (state.isPlaying === true) return
  demoStore.set({ ...state, currentAction: -1, isPlaying: true })
  const fauxtoshop = fauxtoshopStore.get()
  fauxtoshopStore.set({ ...fauxtoshop, guides: [], selection: null })
  doNextAction()
}

export const stopDemo = () => {
  const state = demoStore.get()
  if (state.isPlaying === false) return
  if (lastTimeout) clearTimeout(lastTimeout)
  const fauxtoshop = fauxtoshopStore.get()
  fauxtoshopStore.set({ ...fauxtoshop, guides: [], selection: null })
  demoStore.set({ ...state, isPlaying: false })
}

////////////////////
// Action Methods //
////////////////////

const doWaitAction = (duration: number) => {
  if (lastTimeout) clearTimeout(lastTimeout)
  lastTimeout = setTimeout(() => {
    const { isPlaying } = demoStore.get()
    if (isPlaying) doNextAction()
  }, duration)
}

const doClickAction = (target: ClickActionTarget): void => {
  const { isPlaying } = demoStore.get()
  const handler = clickHandlers[target]
  if (!handler) {
    throw new Error(`No handler exists for ${target}`)
  }

  const element = handler()
  if (!element) return stopDemo()

  getBoundingClientRect(element).then(rect => {
    if (!isPlaying) return

    const state = demoStore.get()
    demoStore.set({
      ...state,
      lastThreeClicks: [
        // Take off the oldest one
        ...state.lastThreeClicks.slice(0, 1),
        // Add a new one
        [rect.x + rect.width / 2, rect.y + rect.height / 2],
      ],
    })

    doNextAction()
  })
}

const doTypeAction = (key: keyof FormState, text: string) => {
  const chars = text.split('')
  let str = ''

  const typeNext = () => {
    if (chars.length === 0) {
      if (str === text) {
        const { isPlaying } = demoStore.get()
        if (isPlaying) doNextAction()
        return
      }
    }

    str += chars.shift()
    if (!typeHandler) {
      throw new Error(`No handler exists for typing`)
    }

    typeHandler(key, str)
    if (lastTimeout) clearTimeout(lastTimeout)
    lastTimeout = setTimeout(typeNext, 20)
  }
  typeNext()
}

const doSelectAction = (rect: SelectAction['rect']) => {
  const { canvas, isPlaying } = demoStore.get()
  const fauxtoshop = fauxtoshopStore.get()
  if (typeof rect === 'function') {
    if (!canvas) throw new Error('Cannot do selection. No canvas specified.')
    rect = rect(canvas)
  }
  fauxtoshopStore.set({
    ...fauxtoshop,
    selection: rect ? { ...rect, animate: true } : null,
  })
  if (isPlaying) doNextAction()
}

///////////////////////////////////////
// Event Listeners and Data Updaters //
///////////////////////////////////////

export const addDemoHandler: DemoHandlerAdder = (
  type: 'type' | ClickActionTarget,
  handler: DemoTypeHandler | DemoClickHandler
) => {
  switch (type) {
    case 'type': {
      typeHandler = handler
      break
    }
    default: {
      clickHandlers[type] = handler as DemoClickHandler
      break
    }
  }
}

export const removeDemoHandler: DemoHandlerRemover = (
  type: 'type' | ClickActionTarget
) => {
  switch (type) {
    case 'type': {
      typeHandler = undefined
      break
    }
    default: {
      delete clickHandlers[type]
      break
    }
  }
}

// TODO: [Prelaunch] update this when the window resizes
export const setCanvas = (canvas: SelectionRect) => {
  const state = demoStore.get()
  demoStore.set({ ...state, canvas })
}
